import { useCallback, useEffect, useState } from 'react';
import {
  DailyTacticalBlockerAlert,
  DailyTacticalOpsReport,
  DailyTacticalImprovementProposals,
  DailyTacticalReportHistory,
  DailyTacticalReadiness,
  DailyTacticalWeeklyAudit,
  DailyTacticalWeeklyReview,
  MarketStatusSimple,
  fetchDailyTacticalBlockerAlert,
  fetchDailyTacticalOpsReport,
  fetchDailyTacticalImprovementProposals,
  fetchDailyTacticalOpsReportHistory,
  fetchDailyTacticalReadiness,
  fetchDailyTacticalWeeklyAudit,
  fetchDailyTacticalWeeklyReview,
  fetchMarketStatusSimple,
} from '../services/operationsApi';

export default function useAtu2LabData() {
  const [readiness, setReadiness] = useState<DailyTacticalReadiness | null>(null);
  const [market, setMarket] = useState<MarketStatusSimple | null>(null);
  const [weeklyAudit, setWeeklyAudit] = useState<DailyTacticalWeeklyAudit | null>(null);
  const [opsReport, setOpsReport] = useState<DailyTacticalOpsReport | null>(null);
  const [weeklyReview, setWeeklyReview] = useState<DailyTacticalWeeklyReview | null>(null);
  const [blockerAlert, setBlockerAlert] = useState<DailyTacticalBlockerAlert | null>(null);
  const [reportHistory, setReportHistory] = useState<DailyTacticalReportHistory | null>(null);
  const [proposals, setProposals] = useState<DailyTacticalImprovementProposals | null>(null);
  const [weeklyAuditError, setWeeklyAuditError] = useState<string | null>(null);
  const [opsError, setOpsError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [proposalsError, setProposalsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [readinessData, marketData] = await Promise.all([
        fetchDailyTacticalReadiness(),
        fetchMarketStatusSimple(),
      ]);
      setReadiness(readinessData);
      setMarket(marketData);
      const cohortId = auditCohortId(readinessData);
      await Promise.all([
        loadWeeklyAudit(cohortId, setWeeklyAudit, setWeeklyAuditError),
        loadOps(cohortId, setOpsReport, setWeeklyReview, setBlockerAlert, setOpsError),
        loadReportVisibility(
          cohortId, setReportHistory, setProposals, setHistoryError, setProposalsError,
        ),
      ]);
      setRefreshedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '2.0 검증실 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return {
    readiness, market, weeklyAudit, opsReport, weeklyReview, blockerAlert, reportHistory, proposals,
    weeklyAuditError, opsError, historyError, proposalsError, loading, error, refreshedAt, load,
  };
}

async function loadReportVisibility(
  cohortId: string,
  setHistory: (value: DailyTacticalReportHistory | null) => void,
  setProposals: (value: DailyTacticalImprovementProposals | null) => void,
  setHistoryError: (value: string | null) => void,
  setProposalsError: (value: string | null) => void,
) {
  const [history, proposals] = await Promise.allSettled([
    fetchDailyTacticalOpsReportHistory(cohortId),
    fetchDailyTacticalImprovementProposals(cohortId),
  ]);
  setHistory(history.status === 'fulfilled' ? history.value : null);
  setHistoryError(history.status === 'rejected' ? errorMessage(history.reason) : null);
  setProposals(proposals.status === 'fulfilled' ? proposals.value : null);
  setProposalsError(proposals.status === 'rejected' ? errorMessage(proposals.reason) : null);
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

async function loadWeeklyAudit(
  cohortId: string,
  setAudit: (audit: DailyTacticalWeeklyAudit | null) => void,
  setAuditError: (error: string | null) => void,
) {
  setAuditError(null);
  try {
    setAudit(await fetchDailyTacticalWeeklyAudit(cohortId));
  } catch (err) {
    setAudit(null);
    setAuditError(err instanceof Error ? err.message : 'weekly audit 조회 실패');
  }
}

async function loadOps(
  cohortId: string,
  setOpsReport: (value: DailyTacticalOpsReport | null) => void,
  setWeeklyReview: (value: DailyTacticalWeeklyReview | null) => void,
  setBlockerAlert: (value: DailyTacticalBlockerAlert | null) => void,
  setOpsError: (error: string | null) => void,
) {
  setOpsError(null);
  try {
    const [ops, weekly, alert] = await Promise.all([
      fetchDailyTacticalOpsReport(cohortId),
      fetchDailyTacticalWeeklyReview(cohortId),
      fetchDailyTacticalBlockerAlert(cohortId),
    ]);
    setOpsReport(ops);
    setWeeklyReview(weekly);
    setBlockerAlert(alert);
  } catch (err) {
    setOpsReport(null);
    setWeeklyReview(null);
    setBlockerAlert(null);
    setOpsError(err instanceof Error ? err.message : 'ops automation 조회 실패');
  }
}

function auditCohortId(readiness: DailyTacticalReadiness) {
  const mode = readiness.flags.cohort_mode;
  if (mode === 'official') return readiness.official_cohort_id;
  if (mode === 'plumbing') return readiness.plumbing_cohort_id;
  return readiness.rehearsal_cohort_id;
}
